<!-- 企業情報入力フォーム: 法人名・住所・部署・名前・連絡先等の一式 -->
<template>
  <div>
    <!-- 法人名 -->
    <v-row class="form-row" no-gutters>
      <v-col cols="12" md="3" class="form-label">
        {{ companyLabel }}
        <span class="req">必須</span>
      </v-col>
      <v-col cols="12" md="9" class="pl-md-12">
        <v-text-field
          :model-value="modelValue.company"
          @update:model-value="updateField('company', $event)"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :rules="[rules.required]"
        />
      </v-col>
    </v-row>

    <!-- 所在地 -->
    <AddressInputGroup
      :model-value="modelValue"
      @update:model-value="emit('update:modelValue', $event)"
      :rules="rules"
      :prefectures="prefectures"
      :require-address="requireAddress"
      @search-address="emit('search-address')"
    />

    <!-- 所属部署 / 役職名 -->
    <v-row class="form-row" no-gutters>
      <v-col cols="12" md="3" class="form-label">
        所属部署 / 役職名
        <span v-if="requireDepartment" class="req">必須</span>
      </v-col>
      <v-col cols="12" md="9" class="form-input pl-md-12">
        <v-text-field
          :model-value="modelValue.department"
          @update:model-value="updateField('department', $event)"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :rules="requireDepartment ? [rules.required] : []"
        />
      </v-col>
    </v-row>

    <!-- お名前 -->
    <v-row class="form-row" no-gutters>
      <v-col cols="12" md="3" class="form-label">
        お名前
        <span class="req">必須</span>
      </v-col>
      <v-col cols="12" md="9" class="form-input pl-md-12">
        <v-text-field
          :model-value="modelValue.name"
          @update:model-value="updateField('name', $event)"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :rules="[rules.required]"
        />
      </v-col>
    </v-row>

    <!-- フリガナ -->
    <v-row class="form-row" no-gutters>
      <v-col cols="12" md="3" class="form-label">
        お名前（フリガナ）
        <span class="req">必須</span>
      </v-col>
      <v-col cols="12" md="9" class="form-input pl-md-12">
        <v-text-field
          :model-value="modelValue.furigana"
          @update:model-value="updateField('furigana', $event)"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :rules="[rules.requiredKatakana]"
        />
      </v-col>
    </v-row>

    <!-- メールアドレス -->
    <v-row class="form-row" no-gutters>
      <v-col cols="12" md="3" class="form-label">
        メールアドレス
        <span class="req">必須</span>
      </v-col>
      <v-col cols="12" md="9" class="form-input pl-md-12">
        <v-text-field
          :model-value="modelValue.email"
          @update:model-value="updateField('email', $event)"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :rules="[rules.requiredEmail]"
        />
      </v-col>
    </v-row>

    <!-- 電話番号 -->
    <v-row class="form-row" no-gutters>
      <v-col cols="12" md="3" class="form-label">
        電話番号
        <span class="req">必須</span>
      </v-col>
      <v-col cols="12" md="9" class="form-input pl-md-12">
        <v-text-field
          :model-value="modelValue.tel"
          @update:model-value="updateField('tel', $event)"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :rules="[rules.requiredTel]"
        />
      </v-col>
    </v-row>

    <!-- 製品・サポート情報配信 -->
    <v-row v-if="showMailDelivery" class="form-row" no-gutters align="center">
      <v-col cols="12" md="3" class="form-label">
        製品・サポート情報の配信
        <span class="req">必須</span>
      </v-col>
      <v-col cols="12" md="9" class="form-input pl-md-10 d-flex align-center">
        <v-radio-group
          :model-value="modelValue.mailDelivery"
          @update:model-value="updateField('mailDelivery', $event)"
          inline
          hide-details="auto"
          class="mt-0"
          :rules="[rules.required]"
        >
          <v-radio label="配信を希望する" value="希望" />
          <v-radio label="配信を希望しない" value="不要" />
        </v-radio-group>
      </v-col>
    </v-row>

    <!-- 運用者名（管理者・代理店のみ） -->
    <template v-if="showSubFields">
      <v-row class="form-row" no-gutters>
        <v-col cols="12" md="3" class="form-label"> お名前（運用者等） </v-col>
        <v-col cols="12" md="9" class="pl-md-12">
          <v-text-field
            :model-value="modelValue.subName"
            @update:model-value="updateField('subName', $event)"
            variant="outlined"
            density="compact"
            hide-details="auto"
          />
        </v-col>
      </v-row>

      <v-row class="form-row" no-gutters>
        <v-col cols="12" md="3" class="form-label">
          メールアドレス（運用者等）
        </v-col>
        <v-col cols="12" md="9" class="pl-md-12">
          <v-text-field
            :model-value="modelValue.subEmail"
            @update:model-value="updateField('subEmail', $event)"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :rules="[rules.email]"
          />
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script setup lang="ts">
import AddressInputGroup from "./AddressInputGroup.vue";

interface CompanyData {
  company?: string | null;
  zip?: string | null;
  prefecture?: string | null;
  city?: string | null;
  address1?: string | null;
  building?: string | null;
  department?: string | null;
  name?: string | null;
  furigana?: string | null;
  email?: string | null;
  tel?: string | null;
  mailDelivery?: string | null;
  subName?: string | null;
  subEmail?: string | null;
}

const props = withDefaults(
  defineProps<{
    modelValue: CompanyData;
    rules: Record<string, any>;
    prefectures: string[];
    companyLabel?: string;
    showMailDelivery?: boolean;
    showSubFields?: boolean;
    requireAddress?: boolean;
    requireDepartment?: boolean;
  }>(),
  {
    companyLabel: "法人名 / 団体名",
    showMailDelivery: true,
    showSubFields: false,
    requireAddress: true,
    requireDepartment: true,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: CompanyData];
  "search-address": [];
}>();

function updateField(field: keyof CompanyData, value: any) {
  emit("update:modelValue", { ...props.modelValue, [field]: value });
}
</script>
